/**
 * Encrypted Content Plugin for Lexical
 * Renders encrypted content decorator nodes with decryption support
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getRoot } from 'lexical'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { decrypt } from '../../../../utils/encryption'
import { $isCustomEncryptedNode } from '../nodes'

export interface EncryptedContentPluginProps {
  onEncryptedClick?: (encryptedText: string, encryptionId: string) => void
  onEncryptedContextMenu?: (
    event: React.MouseEvent,
    encryptionId: string,
    kind?: { character?: string; authorHeld?: true },
  ) => void
  mnemonicMap?: Record<string, string>
  /**
   * encryptionId → ciphertext glyphs, present only for nodes the viewer is permitted
   * to read. Such a node renders as plaintext and clicking it toggles to the glyphs.
   */
  glyphMap?: Record<string, string>
  /**
   * Encrypted nodes no key opens — a character's, or one the author holds until he is
   * ready. They are inert: clicking one must not offer a key prompt.
   */
  sealedIds?: Set<string>
  /**
   * Offered on a passage the viewer is the character of — never to the author, whose
   * whole promise is that this is not his decision.
   */
  onCharacterConsent?: (encryptionId: string, anchor: HTMLElement) => void
  isAuthor?: boolean
  readOnly?: boolean
}

// Cache for decryption results
const decryptionCache = new Map<string, string | null>()

interface EncryptedNodeInfo {
  key: string
  encryptionId: string
  encryptedText: string
  /** Present only when the server judged this viewer entitled to know. */
  character?: string
  /** The author holds this passage: no sentence, opened by his own hand. */
  authorHeld?: true
  element: HTMLElement | null
}

export function EncryptedContentPlugin({
  onEncryptedClick,
  onEncryptedContextMenu,
  mnemonicMap = {},
  glyphMap = {},
  sealedIds,
  onCharacterConsent,
  isAuthor = false,
  readOnly = false,
}: EncryptedContentPluginProps) {
  const [editor] = useLexicalComposerContext()
  // Nodes the viewer is permitted to read but has flipped to their ciphertext.
  const [glyphsShown, setGlyphsShown] = useState<Record<string, boolean>>({})
  const [encryptedNodes, setEncryptedNodes] = useState<EncryptedNodeInfo[]>([])
  // Fingerprint of the last applied node list. We skip the React setState when typing
  // doesn't actually change the encrypted-node set — without this, every keystroke
  // triggers a full re-render and portal reconciliation even though nothing changed.
  const lastSignatureRef = useRef<string>('')

  // Collect all encrypted nodes from the editor
  const updateEncryptedNodes = useCallback(() => {
    editor.getEditorState().read(() => {
      const nodes: EncryptedNodeInfo[] = []
      const root = $getRoot()

      const traverse = (node: any) => {
        if ($isCustomEncryptedNode(node)) {
          const key = node.getKey()
          const element = editor.getElementByKey(key)
          nodes.push({
            key,
            encryptionId: node.getEncryptionId(),
            encryptedText: node.getEncryptedText(),
            character: node.getCharacter(),
            authorHeld: node.getAuthorHeld(),
            element,
          })
        }
        if ('getChildren' in node) {
          node.getChildren().forEach(traverse)
        }
      }

      root.getChildren().forEach(traverse)

      const signature = nodes
        .map(
          (n) =>
            `${n.key}|${n.encryptionId}|${n.encryptedText}|${n.character ?? ''}|${n.authorHeld ? '1' : ''}|${n.element ? '1' : '0'}`,
        )
        .join('§')
      if (signature === lastSignatureRef.current) {
        return
      }
      lastSignatureRef.current = signature
      setEncryptedNodes(nodes)
    })
  }, [editor])

  // Update when editor state changes. We skip the full tree walk on text-only updates
  // (`dirtyElements` empty): typing characters inside an existing encrypted node doesn't
  // add or remove encrypted nodes, so the rendered portals are unaffected. The check
  // collapses per-keystroke cost from O(total nodes) to ~zero in the common case.
  useEffect(() => {
    updateEncryptedNodes()

    return editor.registerUpdateListener(({ dirtyElements }) => {
      if (dirtyElements.size === 0) {
        return
      }
      updateEncryptedNodes()
    })
  }, [editor, updateEncryptedNodes])

  // Render encrypted content components into their DOM wrappers
  const renderedNodes = useMemo(() => {
    return encryptedNodes.map(({ key, encryptionId, encryptedText, character, authorHeld, element }) => {
      if (!element) {
        return null
      }

      // A glyph in the map means the viewer may read this node: `encryptedText` is
      // already the plaintext, and the glyphs are the alternate face of the toggle.
      const permittedGlyphs = glyphMap[encryptionId]
      if (permittedGlyphs) {
        const showingGlyphs = Boolean(glyphsShown[encryptionId])
        // The passage is about this viewer, so the decision about it is theirs.
        const isMine = Boolean(character && !isAuthor && onCharacterConsent)
        return createPortal(
          <span
            key={key}
            // `encrypted-readable`: withheld from the world, but open to this viewer.
            // The reader stylesheet uses it to mark the passage in the margin.
            className="encrypted-content encrypted-readable"
            data-section-id={encryptionId}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setGlyphsShown((prev) => ({ ...prev, [encryptionId]: !prev[encryptionId] }))
            }}
            onContextMenu={
              isMine
                ? (e) => {
                    e.preventDefault()
                    onCharacterConsent?.(encryptionId, e.currentTarget as HTMLElement)
                  }
                : undefined
            }
            style={{ display: 'inline', cursor: 'pointer' }}
            title={
              isMine
                ? 'לחיצה מחליפה בין הטקסט לגליפים · לחיצה ימנית לבחירה מה לעשות איתו'
                : showingGlyphs
                  ? 'Click to show the text'
                  : 'Click to show how readers see this'
            }
          >
            {showingGlyphs ? permittedGlyphs : encryptedText}
          </span>,
          element,
        )
      }

      const mnemonic = mnemonicMap[encryptionId]

      // Check cache or decrypt
      let displayText = encryptedText
      let isDecrypted = false

      if (mnemonic && encryptedText) {
        const cacheKey = `${encryptionId}:${mnemonic}`
        let cached = decryptionCache.get(cacheKey)

        if (cached === undefined) {
          const result = decrypt(encryptedText, mnemonic)
          cached = result.success ? result.text : null
          decryptionCache.set(cacheKey, cached)
        }

        if (cached !== null) {
          displayText = cached
          isDecrypted = true
        }
      }

      // A sealed passage opens only when the person holding it decides. There is no key
      // to ask for, so it stays inert — no click, no cursor, no tooltip. Explaining that
      // to the reader is a separate question, deliberately left unanswered here.
      const isSealed = Boolean(sealedIds?.has(encryptionId))

      const handleClick = (e: React.MouseEvent) => {
        if (readOnly && !isDecrypted && !isSealed && onEncryptedClick) {
          e.preventDefault()
          e.stopPropagation()
          onEncryptedClick(encryptedText, encryptionId)
        }
      }

      // Unmarking is an editing action, so it belongs to the editor. Offering it while
      // the author is reading puts a piece of the workbench in the middle of the book.
      const canUnmark = isAuthor && !readOnly && !isDecrypted

      const handleContextMenu = (e: React.MouseEvent) => {
        if (canUnmark && onEncryptedContextMenu) {
          e.preventDefault()
          onEncryptedContextMenu(e, encryptionId, { character, authorHeld })
        }
      }

      const isLockedForReader = readOnly && !isDecrypted && !isSealed

      return createPortal(
        <span
          key={key}
          className="encrypted-content"
          data-section-id={encryptionId}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          style={{
            display: 'inline',
            cursor: isLockedForReader ? 'pointer' : canUnmark ? 'context-menu' : 'default',
          }}
          title={
            isLockedForReader
              ? 'Click to decrypt'
              : canUnmark
                ? 'Right-click to remove the encryption marking'
                : undefined
          }
        >
          {displayText}
        </span>,
        element,
      )
    })
  }, [
    encryptedNodes,
    mnemonicMap,
    glyphMap,
    glyphsShown,
    sealedIds,
    isAuthor,
    readOnly,
    onCharacterConsent,
    onEncryptedClick,
    onEncryptedContextMenu,
  ])

  return <>{renderedNodes}</>
}
